/**
 * Ad Type tag-support configuration — single source of truth.
 *
 * The authoritative definition lives in App\Enums\AdType (PHP).
 * This module fetches the support map from the backend API so that
 * any future changes to AdType.php are automatically reflected here
 * without touching this file.
 *
 * Usage:
 *   // Call once at app boot (e.g. in main.js / app setup):
 *   import { initAdTypeConfig } from '@/config/adTypes';
 *   await initAdTypeConfig();
 *
 *   // Then use anywhere synchronously:
 *   import { getAdTagSupport, supportsVastTag, supportsInsTag } from '@/config/adTypes';
 */

// AuthBackend is lazily imported at runtime to avoid window-access errors
// in test environments (Vitest/Node). Tests use seedAdTypeConfig() instead.

// ---------------------------------------------------------------------------
// Internal mutable store — populated by initAdTypeConfig()
// Shape: { [adTypeValue: string]: { vast: boolean, ins: boolean } }
// ---------------------------------------------------------------------------
let _supportMap = null;
let _initPromise = null;

/**
 * Fallback: the "expandable_ads" legacy type is not an enum case but
 * must still be handled.  Keep a minimal static override list for
 * types that are intentionally outside the enum.
 */
const STATIC_OVERRIDES = Object.freeze({
    expandable_ads: { vast: false, ins: true },
});

// ---------------------------------------------------------------------------
// Bootstrap
// ---------------------------------------------------------------------------

/**
 * Fetch the tag-support map from the backend and cache it.
 * Safe to call multiple times — the request is deduplicated.
 *
 * @returns {Promise<void>}
 */
export const initAdTypeConfig = () => {
    if (_initPromise) return _initPromise;

    _initPromise = import('@/utils/authBackend')
        .then(({ AuthBackend }) => AuthBackend.get('creatives/ad-types'))
        .then(({ data }) => {
            const types = data?.data?.ad_types ?? data?.ad_types ?? [];
            _supportMap = {};

            for (const type of types) {
                const key = type.value;
                if (key) {
                    _supportMap[key] = {
                        vast: Boolean(type.supports_vast ?? type.display_vast),
                        ins:  Boolean(type.supports_ins  ?? type.display_ins),
                    };
                }
            }

            // Merge static overrides (e.g. legacy expandable_ads)
            Object.assign(_supportMap, STATIC_OVERRIDES);
        })
        .catch(() => {
            // On failure keep _supportMap as null so helpers fall back gracefully.
            _initPromise = null;
        });

    return _initPromise;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Normalize an ad type string (handles labels, case differences, spaces, and hyphens).
 *
 * @param {string|null|undefined} adType
 * @returns {string}
 */
export const normalizeAdType = (adType) => {
    if (!adType || typeof adType !== 'string') return '';
    return adType.trim().toLowerCase().replace(/[\s-]+/g, '_');
};

/**
 * Look up the support record for a given ad type string.
 * Returns undefined when the type is unknown.
 *
 * @param {string} adType
 * @returns {{ vast: boolean, ins: boolean } | undefined}
 */
const _lookup = (adType) => {
    const key = normalizeAdType(adType);
    if (!key) return undefined;

    const map = _supportMap ?? STATIC_OVERRIDES;
    return map[key] ?? map[adType] ?? undefined;
};

/**
 * Check if an ad type supports VAST tags.
 *
 * @param {string|null|undefined} adType
 * @returns {boolean}
 */
export const supportsVastTag = (adType) => {
    if (!adType) return true;
    const record = _lookup(adType);
    return record !== undefined ? record.vast : true;
};

/**
 * Check if an ad type supports INS tags.
 *
 * @param {string|null|undefined} adType
 * @returns {boolean}
 */
export const supportsInsTag = (adType) => {
    if (!adType) return true;
    const record = _lookup(adType);
    return record !== undefined ? record.ins : true;
};

/**
 * Returns an object with display flags for VAST and INS tags for a given ad type.
 *
 * @param {string|null|undefined} adType
 * @returns {{ displayVAST: boolean, displayINS: boolean }}
 */
export const getAdTagSupport = (adType) => ({
    displayVAST: supportsVastTag(adType),
    displayINS:  supportsInsTag(adType),
});

/**
 * Seed the support map manually (useful in tests or SSR environments
 * where an HTTP round-trip is not desirable).
 *
 * @param {Array<{ value: string, supports_vast: boolean, supports_ins: boolean }>} adTypes
 */
export const seedAdTypeConfig = (adTypes) => {
    _supportMap = {};
    for (const type of adTypes) {
        if (type.value) {
            _supportMap[type.value] = {
                vast: Boolean(type.supports_vast ?? type.display_vast),
                ins:  Boolean(type.supports_ins  ?? type.display_ins),
            };
        }
    }
    Object.assign(_supportMap, STATIC_OVERRIDES);
    // Mark as initialised so initAdTypeConfig() won't overwrite
    _initPromise = Promise.resolve();
};

export default {
    initAdTypeConfig,
    seedAdTypeConfig,
    normalizeAdType,
    supportsVastTag,
    supportsInsTag,
    getAdTagSupport,
};
